import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ChefHat, Clock, Users, Flame, Heart, Moon, Zap, Search, ArrowLeft, Trash2 } from "lucide-react";
import wellnessRecipesImage from "@/assets/wellness-recipes-healthy.jpg";
import { AddRecipeDialog } from "@/components/AddRecipeDialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Recipe {
  id: string;
  title: string;
  description: string;
  prepTime: string;
  servings: number;
  category: string;
  symptoms: string[];
  ingredients: string[];
  instructions: string[];
  nutrients: string[];
  authorName?: string;
  userId?: string;
}

const RECIPES: Recipe[] = [
  {
    id: "1",
    title: "Cooling Cucumber Mint Salad",
    description: "A refreshing salad designed to help with hot flashes and promote hydration",
    prepTime: "15 min",
    servings: 4,
    category: "hot-flashes",
    symptoms: ["Hot Flashes", "Night Sweats"],
    ingredients: [
      "2 large cucumbers, thinly sliced",
      "1/4 cup fresh mint leaves",
      "2 tbsp olive oil",
      "1 tbsp lemon juice",
      "1/4 cup feta cheese (optional)",
      "Salt and pepper to taste"
    ],
    instructions: [
      "Slice cucumbers thinly and place in a large bowl",
      "Add torn mint leaves and crumbled feta",
      "Whisk together olive oil, lemon juice, salt, and pepper",
      "Pour dressing over salad and toss gently",
      "Chill for 10 minutes before serving"
    ],
    nutrients: ["High in water content", "Vitamin K", "Antioxidants"]
  },
  {
    id: "2",
    title: "Omega-Rich Salmon Bowl",
    description: "Packed with omega-3s to support mood and reduce inflammation",
    prepTime: "25 min",
    servings: 2,
    category: "mood",
    symptoms: ["Mood Swings", "Brain Fog"],
    ingredients: [
      "2 salmon fillets (6 oz each)",
      "2 cups quinoa, cooked",
      "1 avocado, sliced",
      "2 cups mixed greens",
      "1 tbsp sesame seeds",
      "2 tbsp soy sauce",
      "1 tsp honey",
      "1 tsp ginger, grated"
    ],
    instructions: [
      "Season salmon with salt and pepper, bake at 400°F for 12-15 minutes",
      "Prepare quinoa according to package directions",
      "Mix soy sauce, honey, and ginger for dressing",
      "Arrange quinoa, greens, and avocado in bowls",
      "Top with salmon and drizzle with dressing",
      "Sprinkle with sesame seeds"
    ],
    nutrients: ["Omega-3 fatty acids", "Protein", "B vitamins", "Magnesium"]
  },
  {
    id: "3",
    title: "Calming Chamomile Oat Bowl",
    description: "A soothing breakfast to promote better sleep and reduce anxiety",
    prepTime: "10 min",
    servings: 1,
    category: "sleep",
    symptoms: ["Insomnia", "Anxiety"],
    ingredients: [
      "1/2 cup rolled oats",
      "1 cup brewed chamomile tea, cooled",
      "1 tbsp almond butter",
      "1 tbsp honey",
      "1/4 cup blueberries",
      "1 tbsp ground flaxseed",
      "Pinch of cinnamon"
    ],
    instructions: [
      "Combine oats and chamomile tea in a jar or bowl",
      "Stir in almond butter, honey, and flaxseed",
      "Refrigerate overnight or for at least 2 hours",
      "Top with blueberries and cinnamon before serving",
      "Enjoy cold or warm gently in microwave"
    ],
    nutrients: ["Complex carbohydrates", "Omega-3s", "Tryptophan", "Antioxidants"]
  },
  {
    id: "4",
    title: "Energy-Boosting Green Smoothie",
    description: "Combat fatigue with nutrient-dense greens and natural energy sources",
    prepTime: "5 min",
    servings: 1,
    category: "energy",
    symptoms: ["Fatigue", "Low Energy"],
    ingredients: [
      "2 cups spinach",
      "1 banana",
      "1/2 cup Greek yogurt",
      "1 tbsp chia seeds",
      "1 cup almond milk",
      "1 tbsp almond butter",
      "1 tsp honey (optional)"
    ],
    instructions: [
      "Add all ingredients to a blender",
      "Blend on high until smooth and creamy",
      "Add more almond milk if needed for desired consistency",
      "Pour into a glass and enjoy immediately"
    ],
    nutrients: ["Iron", "B vitamins", "Protein", "Fiber", "Healthy fats"]
  },
  {
    id: "5",
    title: "Bone-Strengthening Kale & White Bean Soup",
    description: "Rich in calcium and vitamin D to support bone health",
    prepTime: "30 min",
    servings: 6,
    category: "bone-health",
    symptoms: ["Joint Pain", "Bone Health"],
    ingredients: [
      "2 cans white beans, drained",
      "4 cups kale, chopped",
      "1 onion, diced",
      "3 cloves garlic, minced",
      "6 cups vegetable broth",
      "2 tbsp olive oil",
      "1 tsp rosemary",
      "Parmesan cheese for topping"
    ],
    instructions: [
      "Sauté onion and garlic in olive oil until softened",
      "Add vegetable broth and bring to a boil",
      "Add white beans and rosemary, simmer for 10 minutes",
      "Add kale and cook until wilted, about 5 minutes",
      "Season with salt and pepper to taste",
      "Serve hot with a sprinkle of Parmesan cheese"
    ],
    nutrients: ["Calcium", "Vitamin K", "Vitamin D", "Protein", "Fiber"]
  }
];

const Recipes = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [userRecipes, setUserRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recipeToDelete, setRecipeToDelete] = useState<Recipe | null>(null);

  useEffect(() => {
    fetchUserRecipes();
    getCurrentUser();
  }, []);

  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setCurrentUserId(user?.id || null);
  };

  const fetchUserRecipes = async () => {
    try {
      const { data, error } = await supabase
        .from("user_recipes")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;

      if (data) {
        const formattedRecipes: Recipe[] = data.map((recipe) => ({
          id: recipe.id,
          title: recipe.title,
          description: recipe.description,
          prepTime: recipe.prep_time,
          servings: recipe.servings,
          category: recipe.category,
          symptoms: recipe.symptoms,
          ingredients: (recipe.ingredients as string[]) || [],
          instructions: (recipe.instructions as string[]) || [],
          nutrients: recipe.nutrients,
          authorName: recipe.author_name,
          userId: recipe.user_id,
        }));
        setUserRecipes(formattedRecipes);
      }
    } catch (error: any) {
      console.error("Error fetching user recipes:", error);
      toast({
        title: "Error",
        description: "Failed to load user recipes",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (recipe: Recipe) => {
    setRecipeToDelete(recipe);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!recipeToDelete) return;

    try {
      const { error } = await supabase
        .from("user_recipes")
        .delete()
        .eq("id", recipeToDelete.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Recipe deleted successfully",
      });

      fetchUserRecipes();
    } catch (error: any) {
      console.error("Error deleting recipe:", error);
      toast({
        title: "Error",
        description: "Failed to delete recipe",
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setRecipeToDelete(null);
    }
  };

  const allRecipes = [...RECIPES, ...userRecipes];

  const filteredRecipes = allRecipes.filter(recipe => {
    const matchesSearch = recipe.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         recipe.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || recipe.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "hot-flashes":
        return <Flame className="h-4 w-4 text-orange-500" />;
      case "mood":
        return <Heart className="h-4 w-4 text-pink-500" />;
      case "sleep":
        return <Moon className="h-4 w-4 text-blue-500" />;
      case "energy":
        return <Zap className="h-4 w-4 text-yellow-500" />;
      case "bone-health":
        return <Heart className="h-4 w-4 text-teal-500" />;
      default:
        return <ChefHat className="h-4 w-4 text-primary" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "hot-flashes":
        return "border-orange-500/20 hover:border-orange-500/50";
      case "mood":
        return "border-pink-500/20 hover:border-pink-500/50";
      case "sleep":
        return "border-blue-500/20 hover:border-blue-500/50";
      case "energy":
        return "border-yellow-500/20 hover:border-yellow-500/50";
      case "bone-health":
        return "border-teal-500/20 hover:border-teal-500/50";
      default:
        return "border-primary/20 hover:border-primary/50";
    }
  };

  const getSymptomBadgeColor = (symptom: string) => {
    if (symptom.includes("Hot Flash") || symptom.includes("Night Sweat")) {
      return "bg-orange-500/10 text-orange-700 border-orange-500/20";
    } else if (symptom.includes("Mood") || symptom.includes("Anxiety")) {
      return "bg-pink-500/10 text-pink-700 border-pink-500/20";
    } else if (symptom.includes("Insomnia") || symptom.includes("Sleep")) {
      return "bg-blue-500/10 text-blue-700 border-blue-500/20";
    } else if (symptom.includes("Fatigue") || symptom.includes("Energy")) {
      return "bg-yellow-500/10 text-yellow-700 border-yellow-500/20";
    } else if (symptom.includes("Joint") || symptom.includes("Bone")) {
      return "bg-teal-500/10 text-teal-700 border-teal-500/20";
    } else if (symptom.includes("Brain Fog")) {
      return "bg-purple-500/10 text-purple-700 border-purple-500/20";
    }
    return "bg-secondary text-secondary-foreground";
  };

  return (
    <div className="min-h-screen bg-background relative">
      {/* Background Image with Fade */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url(${wellnessRecipesImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/90 to-background/95" />
      </div>

      {/* Content */}
      <div className="relative z-10">
        <div className="container mx-auto px-4 py-8 pb-32 max-w-6xl">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <Button
                variant="ghost"
                onClick={() => navigate("/")}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <AddRecipeDialog onRecipeAdded={fetchUserRecipes} />
            </div>
            <h1 className="text-4xl font-bold mb-4 text-foreground">Wellness Recipes</h1>
            <p className="text-lg text-muted-foreground">
              Nourish your body with menopause-friendly meals designed for symptom relief and vitality
            </p>
          </div>

          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search recipes..."
                value={searchQuery}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchQuery(value);
                  if (value.trim().length > 0) {
                    setSelectedCategory("all");
                  }
                }}
                className="pl-10"
              />
            </div>
          </div>

          {/* Category Tabs */}
          <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="mb-8">
            <TabsList className="grid w-full grid-cols-6 lg:w-auto lg:inline-grid animate-fade-in">
              <TabsTrigger value="all" className="transition-all duration-300 data-[state=active]:scale-105 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                All
              </TabsTrigger>
              <TabsTrigger value="hot-flashes" className="transition-all duration-300 data-[state=active]:scale-105 data-[state=active]:bg-orange-500 data-[state=active]:text-white">
                <Flame className="h-4 w-4 mr-1" />
                Hot Flashes
              </TabsTrigger>
              <TabsTrigger value="mood" className="transition-all duration-300 data-[state=active]:scale-105 data-[state=active]:bg-pink-500 data-[state=active]:text-white">
                <Heart className="h-4 w-4 mr-1" />
                Mood
              </TabsTrigger>
              <TabsTrigger value="sleep" className="transition-all duration-300 data-[state=active]:scale-105 data-[state=active]:bg-blue-500 data-[state=active]:text-white">
                <Moon className="h-4 w-4 mr-1" />
                Sleep
              </TabsTrigger>
              <TabsTrigger value="energy" className="transition-all duration-300 data-[state=active]:scale-105 data-[state=active]:bg-yellow-500 data-[state=active]:text-white">
                <Zap className="h-4 w-4 mr-1" />
                Energy
              </TabsTrigger>
              <TabsTrigger value="bone-health" className="transition-all duration-300 data-[state=active]:scale-105 data-[state=active]:bg-teal-500 data-[state=active]:text-white">
                <Heart className="h-4 w-4 mr-1" />
                Bone Health
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Recipe Accordion */}
          <Accordion type="single" collapsible className="space-y-4">
            {filteredRecipes.map((recipe, index) => (
              <AccordionItem 
                key={recipe.id} 
                value={recipe.id}
                className={`border rounded-lg overflow-hidden transition-all duration-300 hover:shadow-xl animate-fade-in ${getCategoryColor(recipe.category)}`}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <div className="flex items-center justify-between w-full text-left">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold mb-1">{recipe.title}</h3>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {recipe.prepTime}
                        </div>
                        <div className="flex items-center gap-1">
                          <Users className="h-4 w-4" />
                          {recipe.servings} servings
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 mr-2 items-center">
                      {recipe.userId && recipe.userId === currentUserId && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(recipe);
                          }}
                          className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                      {getCategoryIcon(recipe.category)}
                    </div>
                  </div>
                </AccordionTrigger>
                
                <AccordionContent className="px-6 pb-4">
                  <div className="space-y-4 pt-2">
                    <p className="text-muted-foreground">{recipe.description}</p>
                    
                    {recipe.authorName && (
                      <p className="text-sm text-muted-foreground">
                        By {recipe.authorName}
                      </p>
                    )}
                    
                    <div className="flex flex-wrap gap-2">
                      {recipe.symptoms.map((symptom) => (
                        <Badge key={symptom} className={`border ${getSymptomBadgeColor(symptom)}`}>
                          {symptom}
                        </Badge>
                      ))}
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Ingredients:</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                        {recipe.ingredients.map((ingredient, idx) => (
                          <li key={idx}>{ingredient}</li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Instructions:</h4>
                      <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                        {recipe.instructions.map((instruction, idx) => (
                          <li key={idx}>{instruction}</li>
                        ))}
                      </ol>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">Key Nutrients:</h4>
                      <div className="flex flex-wrap gap-2">
                        {recipe.nutrients.map((nutrient) => (
                          <Badge key={nutrient} variant="outline">
                            {nutrient}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {filteredRecipes.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground text-lg">
                No recipes found. Try adjusting your search or filters.
              </p>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Recipe</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{recipeToDelete?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
};

export default Recipes;
